
export default function BenefitsSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Real Benefits for <span className="text-teal-600">El Paso Small Businesses</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our clients see measurable results from their professional websites. Here's how a quality website 
            transforms small businesses in El Paso and drives real growth.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Increase Your Customer Base by 300%
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Small businesses with professional websites consistently attract 3x more customers than those without. 
              Your website works as a 24/7 sales representative, showcasing your services, building trust with 
              potential customers, and converting visitors into paying clients.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              In El Paso's competitive market, having a website isn't just helpful—it's essential. Customers 
              expect to find businesses online, and without a professional web presence, you're losing potential 
              customers to competitors every day.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-add-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Generate More Leads</h4>
                  <p className="text-gray-600">
                    Contact forms, phone calls, and quote requests flow directly from your website to your business.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Improve Brand Visibility</h4>
                  <p className="text-gray-600">
                    SEO optimization helps your El Paso business appear when customers search for your services.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-handshake-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Build Customer Trust</h4>
                  <p className="text-gray-600">
                    Professional design and customer testimonials establish credibility and encourage purchases.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/000ea3af3a2bca8c650c8181d9b2205c.jpg"
              alt="Small business success and growth through professional website"
              className="w-full h-80 object-cover rounded-2xl shadow-xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">85%</div>
            <div className="text-xl font-semibold text-gray-900 mb-3">Increased Revenue</div>
            <p className="text-gray-600 leading-relaxed">
              Small businesses report an average 85% increase in revenue within 6 months of launching 
              their professional website.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">67%</div>
            <div className="text-xl font-semibold text-gray-900 mb-3">More Local Customers</div>
            <p className="text-gray-600 leading-relaxed">
              Local SEO optimization helps businesses attract 67% more customers from their 
              immediate geographic area.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">24/7</div>
            <div className="text-xl font-semibold text-gray-900 mb-3">Customer Access</div>
            <p className="text-gray-600 leading-relaxed">
              Your website works around the clock, generating leads and providing information even 
              when your business is closed.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=small%20business%20competing%20with%20large%20corporations%20online%2C%20David%20vs%20Goliath%20digital%20marketing%20concept%2C%20professional%20website%20leveling%20playing%20field%2C%20competitive%20advantage%20visualization&width=600&height=400&seq=competition-advantage-9&orientation=landscape"
                alt="Small businesses competing effectively online with professional websites"
                className="w-full h-64 object-cover rounded-xl shadow-lg"
              />
            </div>
            
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Compete with Enterprise Companies
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                A professionally designed website levels the playing field between small businesses and 
                large corporations. When customers find your website through Google searches, they can't 
                tell if you're a small local business or a major company—they just see quality and 
                professionalism.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                This is especially powerful in El Paso, where many customers prefer supporting local 
                businesses. Your website can highlight your personal service, community involvement, 
                and local expertise—advantages that large corporations can't match.
              </p>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Key Competitive Advantages:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-teal-600"></i>
                    <span>Personal, local service that big companies can't provide</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-teal-600"></i>
                    <span>Faster response times and direct owner involvement</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-teal-600"></i>
                    <span>Deep understanding of El Paso market and customers</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-teal-600"></i>
                    <span>Flexible pricing and customized solutions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
